import { useCallback, useMemo, useState } from 'preact/hooks';
import { SystemsFilters } from '@core/configs/admin/systems/table/systems-table.config';
import { normalizeSystemSearchOption } from '@core/utils/filters/normalize-search-option';

export function useSystemFilters() {
    const [sorting, setSorting] = useState(() => ({
        sortByColumn: SystemsFilters?.sorting?.sortByColumn ?? 'systemName',
        sortByDescending: SystemsFilters?.sorting?.sortByDescending ?? true,
    }));

    const [pagination, setPagination] = useState(() => ({
        pageNumber: SystemsFilters?.pagination?.paginationData?.pageNumber ?? 1,
        pageSize: SystemsFilters?.pagination?.paginationData?.pageSize ?? 10,
    }));

    const [search, setSearch] = useState(() => ({
        searchTerm: SystemsFilters?.search?.searchTerm ?? '',
        searchOption: normalizeSystemSearchOption(SystemsFilters?.search?.searchOption),
    }));

    const [filters, setFilters] = useState(() => ({
        role: SystemsFilters?.filters?.role ?? '',
        status: SystemsFilters?.filters?.status,
    }));

    const requestParams = useMemo(() => {
        return {
            pageNumber: pagination.pageNumber,
            pageSize: pagination.pageSize,
            sortByColumn: sorting.sortByColumn,
            sortByDescending: sorting.sortByDescending,
            ...(search.searchTerm ? { searchTerm: search.searchTerm } : {}),
            ...(search.searchOption ? { searchOption: search.searchOption } : {}),
            ...(filters.active !== null ? { active: filters.active } : { active: null }),
        };
    }, [
        pagination.pageNumber,
        pagination.pageSize,
        sorting.sortByColumn,
        sorting.sortByDescending,
        search.searchTerm,
        search.searchOption,
        filters.active,
    ]);

    const sortingCallback = useCallback((incoming) => {
        setSorting((prev) => ({ ...prev, ...incoming }));
        setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    }, []);

    const paginationCallback = useCallback((incoming) => {
        setPagination((prev) => ({ ...prev, ...incoming }));
    }, []);

    const searchCallback = useCallback((searchTerm, searchOption) => {
        const normalizedSearchOption = normalizeSystemSearchOption(searchOption);

        setSearch((prev) => ({
            ...prev,
            searchTerm,
            searchOption: normalizedSearchOption,
        }));
        setPagination((p) => ({ ...p, pageNumber: 1 }));
    }, []);

    const filtersCallback = useCallback((incoming) => {
        setFilters((prev) => ({
            ...prev,
            ...(incoming ? { active: incoming?.isActive?.value } : { active: null }),
        }));
        setPagination((p) => ({ ...p, pageNumber: 1 }));
    }, []);

    const tableFilters = useMemo(() => {
        const base = {
            ...SystemsFilters,
            sorting: {
                ...(SystemsFilters?.sorting ?? {}),
                ...sorting,
                callback: sortingCallback,
            },
            pagination: {
                ...(SystemsFilters?.pagination ?? {}),
                paginationData: { ...pagination },
                paginationEvent: paginationCallback,
            },
        };

        if (SystemsFilters?.advancedFilters) {
            base.advancedFilters = {
                config: {
                    ...(SystemsFilters.advancedFilters.config || {}),
                },
                callbacks: {
                    search: searchCallback,
                    filter: filtersCallback,
                },
            };
        }

        return base;
    }, [
        searchCallback,
        filtersCallback,
        sortingCallback,
        paginationCallback,
        sorting.sortByColumn,
        sorting.sortByDescending,
        pagination.pageNumber,
        pagination.pageSize,
        filters.active,
    ]);

    return {
        requestParams,
        tableFilters,
    };
}
